import { Component, Input, OnInit, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { NbDialogRef } from '@nebular/theme';
import {
  BuildingEquipmentItemDto,
  BuildingEquipmentService,
  BuildingItemDto,
  BuildingService,
  ProtocolTypeItemDto,
  ProtocolTypeService,
} from '@artemis-software/wr-api';

export interface ProtocolCreateResult {
  buildingId: string | undefined;
  buildingEquipmentId: string | undefined;
  protocolTypeId: string;
}

@Component({
  standalone: false,
  selector: 'wr-protocol-create-dialog',
  templateUrl: './protocol-create-dialog.component.html',
  styleUrls: ['./protocol-create-dialog.component.scss'],
})
export class ProtocolCreateDialogComponent implements OnInit {

  @Input() preselectedBuildingId: string | null = null;
  @Input() preselectedBuildingEquipmentId: string | null = null;
  @Input() initialMode: 'building' | 'tga' = 'building';

  readonly loading$ = new BehaviorSubject(false);
  readonly protocolTypes$ = new BehaviorSubject<ProtocolTypeItemDto[]>([]);
  readonly buildingEquipments$ = new BehaviorSubject<BuildingEquipmentItemDto[]>([]);

  readonly mode = signal<'building' | 'tga'>('building');

  selectedBuilding: BuildingItemDto | null = null;
  selectedBuildingForTga: BuildingItemDto | null = null;
  selectedBuildingEquipmentId: string | null = null;
  selectedBuildingEquipment: BuildingEquipmentItemDto | null = null;
  selectedProtocolTypeId: string | null = null;

  constructor(
    private readonly dialogRef: NbDialogRef<ProtocolCreateDialogComponent>,
    private readonly protocolTypeService: ProtocolTypeService,
    private readonly buildingService: BuildingService,
    private readonly buildingEquipmentService: BuildingEquipmentService,
  ) {}

  ngOnInit(): void {
    if (this.initialMode === 'tga' && !this.preselectedBuildingEquipmentId) {
      this.mode.set('tga');
      if (this.preselectedBuildingId) {
        this.buildingService.findById(this.preselectedBuildingId).subscribe(building => {
          this.selectedBuildingForTga = building as unknown as BuildingItemDto;
          this.loadBuildingEquipments(building.id);
        });
      }
      this.loadProtocolTypes();
    } else if (this.preselectedBuildingEquipmentId) {
      this.mode.set('tga');
      this.buildingEquipmentService.findById(this.preselectedBuildingEquipmentId).subscribe(be => {
        this.selectedBuildingEquipmentId = be.id;
        this.selectedBuildingForTga = be.building as unknown as BuildingItemDto;
        this.loadBuildingEquipments(be.building.id);
        this.loadProtocolTypes(be.equipment.id);
      });
    } else {
      this.loadProtocolTypes();
      if (this.preselectedBuildingId) {
        this.buildingService.findById(this.preselectedBuildingId).subscribe(building => {
          this.selectedBuilding = building as unknown as BuildingItemDto;
        });
      }
    }
  }

  setMode(mode: 'building' | 'tga'): void {
    this.mode.set(mode);
    this.selectedBuildingEquipment = null;
    this.selectedBuildingEquipmentId = null;
    this.selectedBuildingForTga = null;
    this.buildingEquipments$.next([]);
    this.selectedProtocolTypeId = null;
    this.loadProtocolTypes();
  }

  onBuildingForTgaChange(building: BuildingItemDto | null): void {
    this.selectedBuildingForTga = building;
    this.selectedBuildingEquipment = null;
    this.selectedBuildingEquipmentId = null;
    this.buildingEquipments$.next([]);
    this.selectedProtocolTypeId = null;
    if (building?.id) {
      this.loadBuildingEquipments(building.id);
    }
    this.loadProtocolTypes();
  }

  onBuildingEquipmentChange(id: string | null): void {
    this.selectedBuildingEquipmentId = id;
    const be = this.buildingEquipments$.value.find(e => e.id === id) ?? null;
    this.selectedBuildingEquipment = be;
    this.selectedProtocolTypeId = null;
    if (be?.equipment?.id) {
      this.loadProtocolTypes(be.equipment.id);
    } else {
      this.loadProtocolTypes();
    }
  }

  loadProtocolTypes(equipmentId?: string): void {
    this.protocolTypeService.findAll({ equipmentId }).subscribe(types => {
      this.protocolTypes$.next(types);
    });
  }

  loadBuildingEquipments(buildingId: string): void {
    this.buildingEquipmentService.findAll({ buildingId, size: 500 }).subscribe(items => {
      this.buildingEquipments$.next(items);
    });
  }

  confirm(): void {
    if (!this.selectedProtocolTypeId) return;
    if (this.mode() === 'tga' && !this.selectedBuildingEquipmentId) return;
    this.dialogRef.close({
      buildingId: this.mode() === 'building' ? (this.selectedBuilding?.id ?? undefined) : undefined,
      buildingEquipmentId: this.mode() === 'tga' ? (this.selectedBuildingEquipmentId ?? undefined) : undefined,
      protocolTypeId: this.selectedProtocolTypeId,
    } as ProtocolCreateResult);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  get canConfirm(): boolean {
    if (!this.selectedProtocolTypeId) return false;
    if (this.mode() === 'tga') return !!this.selectedBuildingEquipmentId;
    return true;
  }
}
